import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { useNodeId } from "@xyflow/react";
import { ToggleSwitch } from "@zapier/design-system";
import {
  agentAtomFamily_SUSPENDS,
  AgentFetchResult,
  isDraftOrTemplateNodeAtomFamily,
} from "@zapier/canvas-state";

import { ErrorBoundary } from "../../ErrorBoundary";
import { UnlinkedStatusIndicator } from "../../UnlinkedStatusIndicator";
import { ToggleContainer } from "../ToggleContainer";
import { LoadingToggle } from "../LoadingToggle";
import { getAgentTooltipContent, isAgentAlwaysEnabled } from "../utils";

interface AgentToggleProps {
  agentId: string | null;
}

function AgentToggleSuspended({ agentId }: { agentId: string }) {
  const agentResult = useAtomValue(agentAtomFamily_SUSPENDS(agentId)) as
    | AgentFetchResult
    | null
    | undefined;

  // Handle error states
  if (!agentResult || agentResult.type !== "success") {
    return null;
  }

  const agent = agentResult.data;
  const workflow = agent.workflow;
  const isEnabled = workflow.enabled;
  const alwaysEnabled = isAgentAlwaysEnabled(workflow);

  // Determine tooltip content
  const tooltipContent = getAgentTooltipContent(workflow, false) || "";

  return (
    <ToggleContainer tooltipContent={tooltipContent} ariaLabel={tooltipContent}>
      <ToggleSwitch
        checked={alwaysEnabled || isEnabled}
        // The toggle switch is disabled for agents because we don't have a way to enable/disable them yet
        // TO-DO: When we have a way to enable/disable agents, we will need to add a way to disable the toggle switch in view-only mode
        // just like we do it for Zaps at the moment
        disabled={true}
        showDisabledTooltip={false}
      />
    </ToggleContainer>
  );
}

export function AgentToggle({ agentId }: AgentToggleProps) {
  const nodeId = useNodeId();

  const isDraftOrTemplateNode = useAtomValue(
    isDraftOrTemplateNodeAtomFamily(nodeId),
  );

  if (isDraftOrTemplateNode) {
    return null;
  }

  if (!agentId) {
    return <UnlinkedStatusIndicator />;
  }

  return (
    <ErrorBoundary renderNullOnError>
      <Suspense fallback={<LoadingToggle />}>
        <AgentToggleSuspended agentId={agentId} />
      </Suspense>
    </ErrorBoundary>
  );
}
